import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtNum, LOCATIONS } from "@/lib/utils";
import { useAuth, isLocationUser, homeLocationOf, isAdmin, isQC } from "@/lib/auth";
import { Package, Sun, Moon, Truck, AlertTriangle, CheckCircle2, XCircle, Printer, MapPin } from "lucide-react";

const LOCATION_OPTIONS = ["ALL", ...LOCATIONS];

export default function Dashboard() {
  const { user } = useAuth();
  const canSwitchLocation = isAdmin(user) || isQC(user);
  const locationLocked = isLocationUser(user);
  const homeLoc = homeLocationOf(user);

  const [scope, setScope] = useState(locationLocked ? homeLoc : "ALL");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (loc) => {
    setLoading(true); setErr("");
    try {
      const params = loc && loc !== "ALL" ? { location: loc } : {};
      const r = await api.get("/reports/dashboard", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(scope); }, [fetchData, scope]);

  if (err) return <div className="p-4 sm:p-8" style={{ color: "var(--red)" }}>{err}</div>;
  if (!data) return <div className="p-4 sm:p-8" style={{ color: "var(--text-muted)" }}>Loading...</div>;

  const role = user?.role;
  const isReceiverWarehouse = role === "baikampady_user" || role === "maghveera_user";
  const isFactoryUser = role === "factory_user";

  const allStats = [
    { key: "stock", label: "Bags in Stock", value: fmtNum(data.total_bags_in_stock), sub: `${fmtNum(data.total_kg_in_stock)} kg`, icon: Package, color: "var(--orange)" },
    { key: "pending_qc", label: "Pending QC", value: fmtNum(data.pending_qc), sub: "batches awaiting", icon: AlertTriangle, color: "var(--amber)" },
    { key: "approved", label: "Available Lots", value: fmtNum(data.approved_batches), sub: "active lots", icon: CheckCircle2, color: "var(--green)" },
    { key: "rejected", label: "Rejected", value: fmtNum(data.rejected_batches), sub: "all-time", icon: XCircle, color: "var(--red)" },
  ];

  let stats;
  if (isReceiverWarehouse) {
    stats = allStats.filter((s) => s.key === "stock" || s.key === "approved");
  } else if (isFactoryUser) {
    stats = allStats.filter((s) => s.key !== "approved" && s.key !== "rejected");
  } else {
    stats = allStats;
  }

  const allTodayStats = [
    { key: "day", label: "Day Shift Today", value: fmtNum(data.today_day_shift_bags), sub: "bags produced", icon: Sun },
    { key: "night", label: "Night Shift Today", value: fmtNum(data.today_night_shift_bags), sub: "bags produced", icon: Moon },
    { key: "dispatched", label: "Dispatched Today", value: fmtNum(data.today_dispatched_bags), sub: "bags out", icon: Truck },
  ];
  const todayStats = isReceiverWarehouse
    ? allTodayStats.filter((s) => s.key === "dispatched")
    : allTodayStats;

  const showTrend = !isReceiverWarehouse;

  const trend = data.production_trend_7d || [];
  const maxTrend = Math.max(1, ...trend.map((t) => t.day + t.night));

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      {/* Brand banner */}
      <div
        className="mb-6 flex items-center gap-4 p-5 sm:p-6 rounded-xl relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0d2a4e 0%, #1e5fbf 70%, #2a78d9 100%)",
          boxShadow: "var(--shadow-md)",
          color: "#ffffff",
        }}
        data-testid="dashboard-banner"
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "-40px",
            right: "-40px",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(224,50,43,0.45) 0%, rgba(224,50,43,0) 70%)",
            filter: "blur(6px)",
          }}
        />
        <img
          src="/logo.jpg"
          alt="SM Marine Products"
          style={{
            width: 64, height: 64, objectFit: "contain",
            background: "#fff", padding: 4, borderRadius: 10,
            boxShadow: "var(--shadow-sm)", flexShrink: 0, position: "relative", zIndex: 1,
          }}
        />
        <div className="min-w-0 relative" style={{ zIndex: 1 }}>
          <div className="text-xs font-mono mb-1" style={{ color: "rgba(255,255,255,0.7)" }}>// CONTROL PANEL</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Operations Overview</h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.82)" }}>
            Live snapshot of finished-goods stock, production, and dispatch — SM Marine Products.
          </p>
        </div>

        <div className="relative no-print flex items-center gap-2" style={{ zIndex: 1, marginLeft: "auto" }}>
          <button
            type="button"
            className="btn"
            style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
            onClick={() => { document.body.setAttribute("data-print-title", "Operations Overview"); window.print(); }}
            data-testid="print-page-button"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {/* Location scope selector */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md"
          style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
          data-testid="dashboard-scope-indicator"
        >
          <MapPin size={14} color="var(--orange)" />
          <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>WAREHOUSE SCOPE</span>
          <span className="font-heading text-sm font-semibold" data-testid="dashboard-current-scope">
            {data.scope || "ALL"}
          </span>
        </div>

        {canSwitchLocation && (
          <div className="flex gap-1 rounded-md overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            {LOCATION_OPTIONS.map((loc) => (
              <button
                key={loc}
                onClick={() => setScope(loc)}
                disabled={loading}
                className="px-3 py-2 text-xs font-mono transition-colors"
                style={{
                  background: scope === loc ? "var(--orange)" : "transparent",
                  color: scope === loc ? "#fff" : "var(--text)",
                  borderRight: loc === LOCATION_OPTIONS[LOCATION_OPTIONS.length - 1] ? "none" : "1px solid var(--border)",
                  fontWeight: scope === loc ? 600 : 400,
                }}
                data-testid={`dashboard-loc-${loc.toLowerCase()}`}
              >
                {loc}
              </button>
            ))}
          </div>
        )}

        {locationLocked && (
          <div className="text-xs font-mono" style={{ color: "var(--text-dim)" }}>
            // Locked to your warehouse
          </div>
        )}
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="card p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="stat-label">{s.label}</div>
              <s.icon size={16} color={s.color} strokeWidth={2} />
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Today shifts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {todayStats.map((s) => (
          <div key={s.label} className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <s.icon size={14} color="var(--text-muted)" />
              <div className="stat-label">{s.label}</div>
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* 7-day trend — hidden for receiver warehouses (Baikampady, Maghveera) */}
      {showTrend && (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs font-mono mb-1" style={{ color: "var(--orange)" }}>// 7-DAY PRODUCTION TREND</div>
            <h3 className="font-heading text-lg font-semibold">Bags produced by shift</h3>
          </div>
          <div className="flex gap-4 text-xs">
            <div className="flex items-center gap-2"><div className="w-3 h-3" style={{ background: "var(--orange)" }} />Day</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3" style={{ background: "#3b82f6" }} />Night</div>
          </div>
        </div>
        <div className="flex items-end gap-3 h-48">
          {trend.map((t) => {
            const total = t.day + t.night;
            const totalPct = (total / maxTrend) * 100;
            const dayPct = total ? (t.day / total) * 100 : 0;
            return (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-2">
                <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>{total || ""}</div>
                <div className="w-full flex flex-col justify-end relative" style={{ height: "85%" }}>
                  <div className="w-full transition-all" style={{ height: `${totalPct}%`, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <div style={{ background: "#3b82f6", height: `${100 - dayPct}%` }} />
                    <div style={{ background: "var(--orange)", height: `${dayPct}%` }} />
                  </div>
                </div>
                <div className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
                  {t.date.slice(5)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
